/**
 * Mirrors backend scoring for the 4-category radar (Profitability, Financial Health, Valuation, Growth).
 * Weights must match backend Constants (StockAppUtils.buildScoreRadar).
 */

const GROWTH_WEIGHTS = [1.25, 1.25, 1.0, 1.0, 0.5, 1.0, 1.0];
const PROFIT_WEIGHTS = [1.2, 1.2, 0.9, 0.9, 0.8];
const VALUATION_WEIGHTS = [1.0, 1.0];
const HEALTH_WEIGHTS = [0.9, 1.0, 1.1];

const SCORE_MAX = 3;

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

function get(sr, key, def = 0) {
  const v = sr?.[key];
  return v != null && !Number.isNaN(v) ? v : def;
}

/**
 * Builds radar data from a stock report (same 4 categories as backend).
 * @param {Object} stockReport - stockReport from API (symbol, totalScore, recommendation, *Score fields)
 * @returns {{ symbol, totalScore, totalScoreMax: 39, recommendation, categories: Array<{ name, score, normalizedPercent }> }}
 */
export function buildScoreRadarFromReport(stockReport) {
  if (!stockReport) {
    return {
      symbol: null,
      totalScore: 0,
      totalScoreMax: 39,
      recommendation: "",
      categories: [
        { name: "Profitability", score: 0, maxScore: 3, normalizedPercent: 0 },
        { name: "Financial Health", score: 0, maxScore: 3, normalizedPercent: 0 },
        { name: "Valuation", score: 0, maxScore: 3, normalizedPercent: 0 },
        { name: "Growth", score: 0, maxScore: 3, normalizedPercent: 0 },
      ],
    };
  }

  const sr = stockReport;
  const growthSum =
    GROWTH_WEIGHTS[0] * get(sr, "salesGrowthScore") +
    GROWTH_WEIGHTS[1] * get(sr, "netIncomeGrowthScore") +
    GROWTH_WEIGHTS[2] * get(sr, "cashflowScore") +
    GROWTH_WEIGHTS[3] * get(sr, "fcfGrowthScore") +
    GROWTH_WEIGHTS[4] * get(sr, "growthConsistencyScore") +
    GROWTH_WEIGHTS[5] * get(sr, "pastEpsScore") +
    GROWTH_WEIGHTS[6] * get(sr, "futureEpsScore");
  const profitSum =
    PROFIT_WEIGHTS[0] * get(sr, "roeScore") +
    PROFIT_WEIGHTS[1] * get(sr, "roicScore") +
    PROFIT_WEIGHTS[2] * get(sr, "earningsQualityScore") +
    PROFIT_WEIGHTS[3] * get(sr, "grossMarginScore") +
    PROFIT_WEIGHTS[4] * get(sr, "profitMarginScore");
  const valSum =
    VALUATION_WEIGHTS[0] * get(sr, "pegScore") +
    VALUATION_WEIGHTS[1] * get(sr, "fairValueScore");
  const healthSum =
    HEALTH_WEIGHTS[0] * get(sr, "currentRatioScore") +
    HEALTH_WEIGHTS[1] * get(sr, "dsrScore") +
    HEALTH_WEIGHTS[2] * get(sr, "debtEqScore");

  const sumGw = sum(GROWTH_WEIGHTS);
  const sumPw = sum(PROFIT_WEIGHTS);
  const sumVw = sum(VALUATION_WEIGHTS);
  const sumHw = sum(HEALTH_WEIGHTS);

  const profitScore = sumPw > 0 ? profitSum / sumPw : 0;
  const healthScore = sumHw > 0 ? healthSum / sumHw : 0;
  const valScore = sumVw > 0 ? valSum / sumVw : 0;
  const growthScore = sumGw > 0 ? growthSum / sumGw : 0;

  const round = (x, p) => (Math.round(x * Math.pow(10, p)) / Math.pow(10, p));

  return {
    symbol: sr.symbol,
    totalScore: sr.totalScore ?? 0,
    totalScoreMax: 39,
    recommendation: sr.recommendation ?? "",
    categories: [
      { name: "Profitability", score: round(profitScore, 2), maxScore: SCORE_MAX, normalizedPercent: round((profitScore / SCORE_MAX) * 100, 1) },
      { name: "Financial Health", score: round(healthScore, 2), maxScore: SCORE_MAX, normalizedPercent: round((healthScore / SCORE_MAX) * 100, 1) },
      { name: "Valuation", score: round(valScore, 2), maxScore: SCORE_MAX, normalizedPercent: round((valScore / SCORE_MAX) * 100, 1) },
      { name: "Growth", score: round(growthScore, 2), maxScore: SCORE_MAX, normalizedPercent: round((growthScore / SCORE_MAX) * 100, 1) },
    ],
  };
}

/** 7 radar axes: Sales Growth, Earnings Growth, ROE, ROIC, Fair Value, PEG, D/E. fullName for display; apiName for backend lookup. */
const RADAR_7_AXES = [
  { key: "salesGrowthScore", name: "Sales Growth", fullName: "Sales Growth", apiName: "Sales Growth", category: "Growth" },
  { key: "netIncomeGrowthScore", name: "Earnings Growth", fullName: "Net Income Growth", apiName: "Net Income Growth", category: "Growth" },
  { key: "roeScore", name: "ROE", fullName: "Return on Equity", apiName: "ROE", category: "Profitability" },
  { key: "roicScore", name: "ROIC", fullName: "ROIC", apiName: "ROIC", category: "Profitability" },
  { key: "fairValueScore", name: "Fair Value", fullName: "Fair Value", apiName: "Fair Value", category: "Valuation" },
  { key: "pegScore", name: "PEG", fullName: "PEG Ratio", apiName: "PEG", category: "Valuation" },
  { key: "debtEqScore", name: "D/E", fullName: "Debt/Equity", apiName: "Debt/Equity", category: "Financial Health" },
];

/**
 * Builds 7-axis radar data from a stock report.
 * @param {Object} stockReport
 * @returns {Array<{ name, fullName, score, normalizedPercent, rawScore, maxScore }>}
 */
export function buildRadar7AxesFromReport(stockReport) {
  if (!stockReport) {
    return RADAR_7_AXES.map((a) => ({
      name: a.name,
      fullName: a.fullName,
      score: 0,
      normalizedPercent: 0,
      rawScore: 0,
      maxScore: SCORE_MAX,
    }));
  }
  const sr = stockReport;
  return RADAR_7_AXES.map((axis) => {
    const raw = get(sr, axis.key);
    const pct = (raw / SCORE_MAX) * 100;
    return {
      name: axis.name,
      fullName: axis.fullName,
      score: Math.min(100, Math.round(pct)),
      normalizedPercent: Math.min(100, pct),
      rawScore: raw,
      maxScore: SCORE_MAX,
    };
  });
}

/**
 * Build 7-axis data from scoreRadar.radarDimensions or scoreRadar.dimensions (pick by name).
 * Prefers radarDimensions when available (API returns curated 7 in order).
 * @param {Array<{ name, score }>} dimensions - radarDimensions or dimensions from API
 * @param {boolean} useRadarDimensions - if true, dimensions are already in radar order (from radarDimensions)
 * @returns {Array<{ name, fullName, score, normalizedPercent, rawScore, maxScore }>}
 */
export function buildRadar7AxesFromDimensions(dimensions, useRadarDimensions = false) {
  if (!dimensions || dimensions.length === 0) return [];
  if (useRadarDimensions && dimensions.length >= 7) {
    return dimensions.slice(0, 7).map((d, i) => {
      const axis = RADAR_7_AXES[i] || { name: d.name, fullName: d.name };
      const raw = d.score ?? 0;
      const pct = (raw / 3) * 100;
      return {
        name: axis.name,
        fullName: axis.fullName,
        score: Math.min(100, Math.round(pct)),
        normalizedPercent: Math.min(100, pct),
        rawScore: raw,
        maxScore: SCORE_MAX,
      };
    });
  }
  const list = [];
  RADAR_7_AXES.forEach((axis) => {
    const dim = (dimensions || []).find(
      (d) => d.name === (axis.apiName || axis.fullName) || d.name === axis.fullName || d.name === axis.name
    );
    const raw = dim?.score ?? 0;
    const pct = (raw / 3) * 100;
    list.push({
      name: axis.name,
      fullName: axis.fullName,
      score: Math.min(100, Math.round(pct)),
      normalizedPercent: Math.min(100, pct),
      rawScore: raw,
      maxScore: SCORE_MAX,
    });
  });
  return list;
}
